import type { Point } from './world-types.js';

/** The shapes a party can keep when it walks. The first is the default. */
export const FORMATION_SHAPES = ['line', 'column', 'wedge', 'block', 'ring'] as const;
export type FormationShape = typeof FORMATION_SHAPES[number];
export const isFormationShape = (value: unknown): value is FormationShape => FORMATION_SHAPES.includes(value as FormationShape);

/**
 * `facing` is the way the formation looks (radians; forward is (sin, cos) on the ground, as for `UnitState.heading`). Without it, it looks along the march.
 * `hand` is the side the slot order runs toward (1 or -1); without it the layout takes the one that spares the group a swap. A dragged order sends
 * both, as its preview drew them, so the heroes stand exactly where the preview showed.
 */
export interface FormationOrder { shape: FormationShape; facing?: number; hand?: 1 | -1 }

export const DEFAULT_FORMATION: FormationOrder = { shape: 'line' };

/** A slot's place relative to the formation's centre: `side` to one hand, `ahead` toward its facing. */
export interface FormationOffset { side: number; ahead: number }

/** Where `count` bodies stand about the centre, `gap` apart edge to edge of their lanes, the front of the shape first. Pure geometry. */
export function formationOffsets(shape: FormationShape, count: number, gap: number): FormationOffset[] {
  const centred = (index: number, of: number) => (index - (of - 1) / 2) * gap;
  const offsets: FormationOffset[] = [];
  if (shape === 'line') for (let i = 0; i < count; i++) offsets.push({ side: centred(i, count), ahead: 0 });
  else if (shape === 'column') for (let i = 0; i < count; i++) offsets.push({ side: 0, ahead: -centred(i, count) });
  else if (shape === 'wedge') {
    // A tip, then pairs behind it along two arms; the whole is re-centred front to back so the group's middle stays on the point.
    for (let i = 0; i < count; i++) { const rank = Math.ceil(i / 2), hand = i % 2 ? 1 : -1; offsets.push({ side: i ? hand * rank * gap * .8 : 0, ahead: -rank * gap * .9 }); }
    const middle = offsets.reduce((sum, offset) => sum + offset.ahead, 0) / (count || 1);
    for (const offset of offsets) offset.ahead -= middle;
  } else if (shape === 'block') {
    // Ranks of ceil(sqrt(n)), the last one centred, so ten heroes stand four wide and three deep.
    const wide = Math.max(1, Math.ceil(Math.sqrt(count))), ranks = Math.ceil(count / wide);
    for (let i = 0; i < count; i++) { const rank = Math.floor(i / wide), inRank = Math.min(wide, count - rank * wide); offsets.push({ side: centred(i - rank * wide, inRank), ahead: -centred(rank, ranks) }); }
  } else {
    // A ring whose neighbours stand `gap` apart; one hero stands on the point.
    const radius = count > 1 ? gap / (2 * Math.sin(Math.PI / count)) : 0;
    for (let i = 0; i < count; i++) offsets.push({ side: Math.sin(i / count * Math.PI * 2) * radius, ahead: Math.cos(i / count * Math.PI * 2) * radius });
  }
  return offsets;
}

/** The heading that looks from `from` toward `to`; a march of no length looks the way the line has always run (along x). */
export function marchFacing(from: Point, to: Point): number {
  return Math.hypot(to.x - from.x, to.z - from.z) > .01 ? Math.atan2(to.x - from.x, to.z - from.z) : Math.PI;
}

/** Where a formation halts: each body's place (same order as given), the way the whole looks, the hand its slot order ran toward, and how far the bodies go (summed squares). */
export interface FormationPlan { places: Point[]; facing: number; hand: 1 | -1; travel: number }

/**
 * Lays out the group halting at `target` in `order`'s shape. Without an explicit facing it looks along the march from the group's centre to the
 * target. Slots fill in the order the bodies are given (the party column's order), front of the shape first and then across it (toward the
 * order's hand, or else whichever is nearer), so the first hero takes the tip of a wedge or the head of a column.
 */
export function planFormation(order: FormationOrder, units: (Point & { radius: number })[], target: Point): FormationPlan {
  if (!units.length) return { places: [], facing: order.facing ?? Math.PI, hand: order.hand ?? 1, travel: 0 };
  const gap = Math.max(...units.map(unit => unit.radius)) * 2 + .25;
  const center = { x: units.reduce((sum, unit) => sum + unit.x, 0) / units.length, z: units.reduce((sum, unit) => sum + unit.z, 0) / units.length };
  const facing = order.facing ?? marchFacing(center, target);
  const ahead = { x: Math.sin(facing), z: Math.cos(facing) }, side = { x: -ahead.z, z: ahead.x };
  const slots = formationOffsets(order.shape, units.length, gap).sort((a, b) => b.ahead - a.ahead || a.side - b.side);
  // The shape and its order hold; which hand the order runs toward is free unless the order names it: take the one that sends the group the shorter way (fewer swaps).
  const place = (hand: 1 | -1) => units.map((_, index) => ({ x: target.x + side.x * slots[index].side * hand + ahead.x * slots[index].ahead, z: target.z + side.z * slots[index].side * hand + ahead.z * slots[index].ahead }));
  const travel = (places: Point[]) => places.reduce((sum, place, index) => sum + (place.x - units[index].x) ** 2 + (place.z - units[index].z) ** 2, 0);
  if (order.hand) { const places = place(order.hand); return { places, facing, hand: order.hand, travel: travel(places) }; }
  const left = place(1), right = place(-1), leftTravel = travel(left), rightTravel = travel(right);
  return rightTravel < leftTravel - 1e-9 ? { places: right, facing, hand: -1, travel: rightTravel } : { places: left, facing, hand: 1, travel: leftTravel };
}

/** The destination of each body in `units` (same order) when the group halts at `target`: `planFormation`'s places. */
export function layoutFormation(order: FormationOrder, units: (Point & { radius: number })[], target: Point): Point[] {
  return planFormation(order, units, target).places;
}
